import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { ArrowLeft, Loader2 } from "lucide-react";
import FeedbackHeader from "@/components/feedback/FeedbackHeader";
import { useFeedbackData } from "@/hooks/useFeedbackData";
import RoadmapBoard from "@/components/admin/RoadmapBoard";
import { PublicChangelogList } from "@/components/feedback/PublicChangelogList";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import FeedbackContainer from "@/components/feedback/FeedbackContainer";
import { ChatWidget } from "@/components/chat/ChatWidget";
import { ChangelogSubscribe } from "@/components/feedback/ChangelogSubscribe";
import RoadmapSelector from "@/components/feedback/RoadmapSelector";
import CompanyMetadata from "@/components/shared/CompanyMetadata";
import { type Roadmap } from "@/types";

type CompanyFeedbackProps = {
  customSlug?: string;
};

const CompanyFeedback = ({ customSlug }: CompanyFeedbackProps) => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { toast } = useToast();
  const [company, setCompany] = useState<{
    id: string;
    name: string;
    owner_id: string;
    plan: string;
    logo_url?: string | null;
    slug?: string;
  } | null>(null);
  const [user, setUser] = useState<any>(null);
  const [changelogs, setChangelogs] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [debugInfo, setDebugInfo] = useState<any>(null);
  const [selectedRoadmapId, setSelectedRoadmapId] = useState<string | null>(null);
  const [selectedRoadmap, setSelectedRoadmap] = useState<Roadmap | null>(null);

  // Ensure public pages are always in light mode by removing any theme classes
  useEffect(() => {
    document.documentElement.classList.remove('dark', 'admin-dark');
  }, []);

  const { feedback, companyStatuses, refetchFeedback } = useFeedbackData(company?.id ?? "");

  useEffect(() => {
    const getSession = async () => {
      try {
        const {
          data: { session },
        } = await supabase.auth.getSession();
        setUser(session?.user ?? null);
      } catch (err) {
        console.error("Error getting session:", err);
      }
    };

    getSession();

    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((_event, session) => {
      setUser(session?.user ?? null);
    });

    return () => subscription.unsubscribe();
  }, []);

  useEffect(() => {
    const fetchCompany = async () => {
      setIsLoading(true);
      setError(null);

      const effectiveSlug = customSlug || slug;
      console.log(
        "Fetching company with slug:",
        effectiveSlug,
        "User authenticated:",
        !!user
      );

      if (!effectiveSlug) {
        console.error("No slug provided");
        setError("No company slug provided");
        setIsLoading(false);
        return;
      }

      try {
        console.log(`Querying companies table for slug="${effectiveSlug}"`);

        const { data: companies, error: queryError } = await supabase
          .from("companies")
          .select("id, name, owner_id, plan, logo_url, slug")
          .eq("slug", effectiveSlug)
          .limit(1);

        console.log("Query response:", { companies, error: queryError });
        setDebugInfo({ companies, queryError });

        if (queryError) {
          console.error("Error fetching company:", queryError);
          setError("Error loading company data");
          setIsLoading(false);
          return;
        }

        if (companies && companies.length > 0) {
          console.log("Found company with exact slug match:", companies[0]);
          setCompany(companies[0]);
          setError(null);
          setIsLoading(false);
          return;
        }

        console.log(
          `No exact match found. Trying case-insensitive search for slug="${effectiveSlug}"`
        );

        const { data: similarCompanies, error: likeError } = await supabase
          .from("companies")
          .select("id, name, owner_id, plan, logo_url, slug")
          .ilike("slug", effectiveSlug)
          .limit(10);

        console.log("Case-insensitive search response:", {
          similarCompanies,
          error: likeError,
        });

        if (likeError) {
          console.error("Error in case-insensitive search:", likeError);
          setError("Error loading company data");
          setIsLoading(false);
          return;
        }

        if (similarCompanies && similarCompanies.length > 0) {
          const exactCaseInsensitiveMatch = similarCompanies.find(
            (c) => c.slug?.toLowerCase() === effectiveSlug.toLowerCase()
          );

          if (exactCaseInsensitiveMatch) {
            console.log(
              "Found case-insensitive match:",
              exactCaseInsensitiveMatch
            );
            setCompany(exactCaseInsensitiveMatch);
            setError(null);
            setIsLoading(false);
            return;
          }

          console.log("Found similar companies but no exact match");
          const similarSlugs = similarCompanies
            .filter((c) => c.slug)
            .map((c) => c.slug)
            .join(", ");

          setError(
            `No exact match found for "${effectiveSlug}". Did you mean: ${similarSlugs}?`
          );
          setIsLoading(false);
          return;
        }

        console.log("Attempting direct public schema query as a last resort");

        const { data: publicCompanies, error: publicError } = await supabase
          .from("companies")
          .select("id, name, owner_id, plan, logo_url, slug")
          .filter("slug", "eq", effectiveSlug)
          .limit(1);

        console.log("Public query response:", { publicCompanies, publicError });

        if (!publicError && publicCompanies && publicCompanies.length > 0) {
          console.log("Found company via public query:", publicCompanies[0]);
          setCompany(publicCompanies[0]);
          setError(null);
          setIsLoading(false);
          return;
        }

        console.error(
          `No company found with slug similar to "${effectiveSlug}"`
        );
        setError(`No company found with the name "${effectiveSlug}"`);
        setIsLoading(false);
      } catch (err) {
        console.error("Unexpected error:", err);
        setError("An unexpected error occurred");
        setIsLoading(false);
      }
    };

    fetchCompany();
  }, [slug, customSlug, user]);

  useEffect(() => {
    console.log("CompanyFeedback component state:", {
      slug,
      customSlug,
      company,
      isLoading,
      error,
      isAuthenticated: !!user,
      debugInfo,
    });
  }, [slug, customSlug, company, isLoading, error, user, debugInfo]);

  useEffect(() => {
    if (!company) return;

    const fetchChangelogs = async () => {
      try {
        const { data, error } = await supabase
          .from("changelogs")
          .select("*")
          .eq("company_id", company.id)
          .order("created_at", { ascending: false });

        if (error) {
          console.error("Error fetching changelogs:", error);
          return;
        }

        if (data) {
          setChangelogs(data);
        }
      } catch (err) {
        console.error("Unexpected error fetching changelogs:", err);
      }
    };

    fetchChangelogs();
  }, [company]);

  useEffect(() => {
    const fetchRoadmapData = async () => {
      if (!selectedRoadmapId || !company) return;
      
      try {
        const { data: roadmapData, error: roadmapError } = await supabase
          .from("roadmaps")
          .select("*")
          .eq("id", selectedRoadmapId)
          .single();

        if (roadmapError) {
          console.error("Error fetching roadmap:", roadmapError);
          setSelectedRoadmap(null);
          return;
        }

        const { data: statusesData, error: statusesError } = await supabase
          .from("roadmap_statuses")
          .select("*")
          .eq("roadmap_id", selectedRoadmapId)
          .order("position", { ascending: true });

        if (statusesError) {
          console.error("Error fetching roadmap statuses:", statusesError);
          setSelectedRoadmap({
            ...roadmapData,
            statuses: []
          });
          return;
        }

        setSelectedRoadmap({
          ...roadmapData,
          statuses: statusesData || []
        });
      } catch (error) {
        console.error("Error in fetchRoadmapData:", error);
        setSelectedRoadmap(null);
      }
    };

    fetchRoadmapData();
  }, [selectedRoadmapId, company]);

  const handleVote = async (feedbackId: string) => {
    if (!user) {
      toast({
        title: "Authentication required",
        description: "Please sign in to vote on feedback",
        variant: "destructive",
      });
      return;
    }

    try {
      const { data: existingVote } = await supabase
        .from("feedback_votes")
        .select("id")
        .eq("feedback_id", feedbackId)
        .eq("user_id", user.id)
        .maybeSingle();

      if (existingVote) {
        await supabase
          .from("feedback_votes")
          .delete()
          .eq("id", existingVote.id);
      } else {
        await supabase.from("feedback_votes").insert({
          feedback_id: feedbackId,
          user_id: user.id,
        });
      }

      refetchFeedback();
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to process your vote",
        variant: "destructive",
      });
    }
  };

  const isCompanyOwner = user && company && user.id === company.owner_id;

  // Strict validation for company data to ensure React Helmet receives valid data
  const isValidCompany = company && 
    typeof company === 'object' && 
    typeof company.name === 'string' &&
    company.name.trim().length > 0;

  if (isLoading) {
    return (
      <div className="min-h-screen p-6 bg-background flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="h-8 w-8 animate-spin mx-auto mb-4" />
          <p className="text-muted-foreground">Loading company data...</p>
          <p className="text-xs text-muted-foreground mt-2">
            Looking for: {customSlug || slug}
          </p>
        </div>
      </div>
    );
  }

  if (error || !company) {
    return (
      <div className="min-h-screen p-6 bg-background">
        <div className="max-w-4xl mx-auto text-center">
          <h1 className="text-2xl font-bold">Company not found</h1>
          <p className="text-muted-foreground mt-2">
            {error || "The requested company could not be found."}
          </p>
          <p className="text-xs text-muted-foreground mt-1">
            Slug: {customSlug || slug}
          </p>
          <div className="mt-6 space-y-3">
            <Button
              onClick={() => {
                setError(null);
                setIsLoading(true);
                navigate(`/${customSlug || slug}`);
              }}
            >
              Try Again
            </Button>
            <div>
              <Button variant="outline" onClick={() => navigate("/")}>
                Return to Home
              </Button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      {isLoading ? (
        <div className="flex items-center justify-center min-h-screen">
          <Loader2 className="h-6 w-6 animate-spin" />
        </div>
      ) : error ? (
        <div className="flex flex-col items-center justify-center min-h-screen p-4">
          <p className="text-destructive mb-4">{error}</p>
          <Button onClick={() => navigate("/")}>Go Home</Button>
        </div>
      ) : company ? (
        <div className="container mx-auto px-4 py-8 space-y-8">
          {isValidCompany && <CompanyMetadata company={company} pageType="feedback" />}
          
          <FeedbackHeader
            company={{
              id: company.id,
              name: company.name,
              logo_url: company.logo_url,
              slug: company.slug || customSlug || slug || ""
            }}
          />

          <Tabs defaultValue="list" className="w-full">
            <TabsList className="grid w-full max-w-[400px] grid-cols-3">
              <TabsTrigger value="list">Feedback</TabsTrigger>
              <TabsTrigger value="roadmap">Roadmap</TabsTrigger>
              <TabsTrigger value="changelog">Changelog</TabsTrigger>
            </TabsList>
            <TabsContent value="list">
              <FeedbackContainer
                feedback={feedback}
                companyStatuses={companyStatuses}
                onVote={handleVote}
                companyId={company.id}
              />
            </TabsContent>
            <TabsContent value="roadmap">
              <div className="space-y-4">
                {company && (
                  <RoadmapSelector
                    companyId={company.id}
                    selectedRoadmapId={selectedRoadmapId}
                    onRoadmapSelect={setSelectedRoadmapId}
                  />
                )}
                <RoadmapBoard
                  feedback={feedback}
                  companyStatuses={companyStatuses}
                  onFeedbackUpdate={refetchFeedback}
                  isReadOnly={true}
                  roadmap={selectedRoadmap}
                />
              </div>
            </TabsContent>
            <TabsContent value="changelog">
              <PublicChangelogList
                changelogs={changelogs}
                companyId={company.id}
                companyName={company.name}
                isPro={company.plan === "pro" || company.plan === "starter" || company.plan === "enterprise"}
              />
            </TabsContent>
          </Tabs>

          {company && (
            <ChatWidget
              companyId={company.id}
              companyName={company.name}
              userName={user?.email?.split("@")[0] || ""}
              userId={user?.id}
            />
          )}

          <div className="text-center pt-12 pb-4 text-sm text-muted-foreground">
            <a
              href="https://feednatic.com"
              target="_blank"
              rel="noopener noreferrer"
              className="hover:text-primary transition-colors"
            >
              Powered by Feednatic
            </a>
          </div>

          {isCompanyOwner && (
            <Button
              className="fixed bottom-6 left-6 shadow-lg"
              onClick={() => navigate("/admin")}
              size="lg"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Dashboard
            </Button>
          )}
        </div>
      ) : null}
    </div>
  );
};

export default CompanyFeedback;
